import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  ElementRef,
  ViewChild,
  AfterViewInit,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import {
  Subscription,
  Subject,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
} from 'rxjs';
import { AutofillMonitor, AutofillEvent } from '@angular/cdk/text-field';
import { FocusableOption } from '@angular/cdk/a11y';
import { PopsySkeletonComponent } from '../../skeleton/popsy-skeleton.component';
import { NgIf } from '@angular/common';

const DEBOUNCE_USER_INPUT = 600;

@Component({
  selector: 'popsy-input',
  templateUrl: './popsy-input.template.html',
  styleUrls: ['./popsy-input.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, ReactiveFormsModule, PopsySkeletonComponent],
})
export class PopsyInputComponent
  implements AfterViewInit, OnDestroy, OnChanges, FocusableOption
{
  public static COUNT = 0;
  @Input() public type?: 'text' | 'textarea' | null;
  @Input() public id?: string | null = null;
  @Input() public label?: string | null = null;
  @Input() public placeholder?: string | null = null;
  @Input() public error?: string | null = null;
  @Input() public autocomplete?: string | null = null;
  @Input() public inputmode?: string | null = null;
  @Input() public minlength?: number | null = null;
  @Input() public maxlength?: number | null = null;
  @Input() public focusIndex?: number | null = null;
  @Input() public autofocus?: boolean | null = null;
  @Input() public disabled?: boolean;
  @Input() public initialized?: boolean | null;
  @Input() public required?: boolean | null;
  @Input() public loading?: boolean | null = null;
  @Input() public userChangesOnly?: boolean | null = null;
  @Input() public value?: string | null = null;

  @Output() public readonly finalValueChange: EventEmitter<string>;
  @Output() public readonly valueChange: EventEmitter<string>;
  @Output() public readonly debouncedValueChange: EventEmitter<string>;
  @Output() public readonly statusChange: EventEmitter<string>;
  @Output() public readonly focusChange: EventEmitter<{
    focused: boolean;
    value: string;
  }>;
  @Output() public readonly validateValue: EventEmitter<{
    value: string | null;
    callback: (isValid: boolean) => void;
  }>;

  private readonly debounceUserInputSubject: Subject<string>;
  private intPopsyInputElement?: ElementRef | null;
  @ViewChild('field')
  private set popsyInputElement(elementRef: ElementRef | null) {
    this.intPopsyInputElement = elementRef;
    this.monitorInputElement();

    if (this.autofocus && elementRef?.nativeElement) {
      elementRef.nativeElement.focus();
    }
  }
  private get popsyInputElement(): ElementRef | null {
    return this.intPopsyInputElement || null;
  }
  public readonly generatedId: string;

  private userAction: boolean;
  public inputInitialized: boolean;

  public focused: boolean;

  public autofilled?: boolean;

  public readonly formControl: UntypedFormControl;

  private autofillSubscription?: Subscription | null;
  private readonly formObserver: Subscription | null;

  private readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly autofill: AutofillMonitor
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();
    this.generatedId = `popsy-input-${PopsyInputComponent.COUNT++}`;
    this.formControl = new UntypedFormControl('', []);

    this.finalValueChange = new EventEmitter();
    this.valueChange = new EventEmitter();
    this.debouncedValueChange = new EventEmitter();
    this.statusChange = new EventEmitter();
    this.focusChange = new EventEmitter();
    this.validateValue = new EventEmitter();

    this.focused = false;
    this.userAction = false;
    this.inputInitialized = false;

    this.debounceUserInputSubject = new Subject();
    this.subscriptions.add(
      this.debounceUserInputSubject
        .asObservable()
        .pipe(
          debounceTime(DEBOUNCE_USER_INPUT),
          distinctUntilChanged(),
          takeUntil(this.destroySubject)
        )
        .subscribe({
          next: (text: string) => {
            this.debouncedValueChange.next(text);
          },
        })
    );

    this.formObserver = new Subscription();
    this.formObserver.add(
      this.formControl.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe({
        next: (value: string) => {
          console.log('value', value);
          if (this.userChangesOnly) {
            if (this.userAction) {
              this.valueChange.next(`${value || ''}`);
              this.debounceUserInputSubject.next(`${value || ''}`);
            }
          } else {
            this.valueChange.next(`${value || ''}`);
            this.debounceUserInputSubject.next(`${value || ''}`);
          }
          this.validateValue.next({
            value: value || null,
            callback: this.inputValidationResult.bind(this),
          });
          if (this.inputInitialized) {
            this.finalValueChange.next(`${value || ''}`);
          }
        },
      })
    );

    this.formObserver.add(
      this.formControl.statusChanges.pipe(takeUntil(this.destroySubject)).subscribe({
        next: (status: string) => {
          this.statusChange.next(`${status || ''}`);
        },
      })
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.initialized) {
      this.inputInitialized = Boolean(this.initialized);
      this.changeDetector.markForCheck();
    }

    if (changes?.autofocus && changes.autofocus.isFirstChange()) {
      if (this.autofocus && this.popsyInputElement?.nativeElement) {
        this.popsyInputElement.nativeElement.focus();
      }
    }

    if (
      changes?.value &&
      (this.formControl.value?.length === 0 ||
        this.formControl.value === changes.value.previousValue)
    ) {
      console.log('value changes', this.value);
      this.formControl.setValue(this.value || '');
      this.inputInitialized = Boolean(this.value?.length);
      this.changeDetector.markForCheck();
    }
  }

  public ngAfterViewInit(): void {
    this.monitorInputElement();
  }

  private inputValidationResult(isValid: boolean): void {
    console.log('inputValidationResult', isValid, this.formControl.value);
    if (isValid) {
      this.finalValueChange.next(`${this.formControl.value || ''}`);
      this.inputInitialized = true;
      this.changeDetector.markForCheck();
    }
  }

  public focus(): void {
    if (this.popsyInputElement?.nativeElement) {
      this.popsyInputElement.nativeElement.focus();
    }
  }

  public selectAllText(): void {
    if (this.popsyInputElement?.nativeElement) {
      this.popsyInputElement.nativeElement.setSelectionRange(
        0,
        `${this.popsyInputElement.nativeElement.value || ''}`.length
      );
    }
  }

  private monitorInputElement(): void {
    if (this.popsyInputElement) {
      if (this.popsyInputElement?.nativeElement) {
        if (this.autofocus && this.popsyInputElement?.nativeElement) {
          this.popsyInputElement.nativeElement.focus();
        }

        this.autofillSubscription = this.autofill
          .monitor(this.popsyInputElement.nativeElement)
          .pipe(takeUntil(this.destroySubject))
          .subscribe({
            next: ($event: AutofillEvent) => {
              this.inputInitialized = true;
              this.autofilled = $event.isAutofilled;
              this.finalValueChange.next(`${this.formControl.value || ''}`);
              this.changeDetector.markForCheck();
            },
            error: () => {},
          });
      }
    }
  }

  public onFocused(isFocused?: boolean | null): boolean {
    this.userAction = true;
    if (this.focused && !isFocused) {
      console.log('onFocused', this.formControl.value);
      this.inputInitialized = true;
      this.finalValueChange.next(`${this.formControl.value || ''}`);
      this.changeDetector.markForCheck();
    }

    this.focusChange.next({
      focused: Boolean(isFocused),
      value: `${this.formControl.value || ''}`,
    });
    this.changeDetector.markForCheck();
    return false;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }

    if (this.formObserver) {
      this.formObserver.unsubscribe();
    }

    if (this.autofillSubscription) {
      this.autofillSubscription.unsubscribe();
    }
  }
}
